import {
  ArrowRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import type { Template } from "@reactive-resume/utils";
import { cn } from "@reactive-resume/utils";
import { useMemo, useRef } from "react";

import { RESUME_TEMPLATES } from "@/client/pages/client/user-space/templates/data/templates";

type Props = {
  selected: Template | null;
  busy: boolean;
  onSelect: (template: Template) => void;
  onCreate: () => void;
};

export const TemplateSelectionStep = ({ selected, busy, onSelect, onCreate }: Props) => {
  const carouselRef = useRef<HTMLDivElement>(null);

  /*
   * Une seule source de vérité est utilisée.
   *
   * Aucun slice n’est appliqué : tous les modèles
   * réellement disponibles sont présentés.
   */
  const templates = useMemo(
    () =>
      RESUME_TEMPLATES.filter((template) => Boolean(template.templateId && template.thumbnailUrl)),
    [],
  );

  const moveCarousel = (direction: "previous" | "next") => {
    const carousel = carouselRef.current;

    if (!carousel) {
      return;
    }

    carousel.scrollBy({
      left: direction === "next" ? carousel.clientWidth * 0.9 : -carousel.clientWidth * 0.9,
      behavior: "smooth",
    });
  };

  return (
    <section className="mx-auto w-full max-w-5xl">
      <header>
        <p
          className={cn(
            "text-[10px] font-bold uppercase",
            "tracking-[0.14em] text-emerald-600",
            "sm:text-xs",
          )}
        >
          Bibliothèque de modèles
        </p>

        <h1
          id="create-resume-title"
          className={cn(
            "mt-1 text-2xl font-bold tracking-tight",
            "leading-tight text-indigo-950",
            "sm:text-3xl lg:text-[34px]",
          )}
        >
          Choisissez votre modèle
        </h1>

        <p
          className={cn(
            "mt-1.5 max-w-3xl",
            "text-xs leading-5 text-indigo-950/75",
            "sm:text-sm sm:leading-6",
          )}
        >
          Parcourez tous les modèles disponibles et choisissez celui qui correspond au style de
          votre candidature. Vous pourrez ensuite le personnaliser dans l’éditeur.
        </p>
      </header>

      <div
        className={cn(
          "mt-4 overflow-hidden rounded-2xl",
          "border border-indigo-100 bg-white",
          "p-4 sm:p-5",
          "shadow-[0_5px_18px_rgba(15,23,42,0.04)]",
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                "grid size-9 shrink-0 place-items-center",
                "rounded-xl bg-indigo-50 text-indigo-600",
                "sm:size-10",
              )}
            >
              <SquaresFourIcon aria-hidden size={22} weight="duotone" />
            </span>

            <div className="min-w-0">
              <h2 className={cn("text-xs font-bold text-indigo-950", "sm:text-sm")}>
                {templates.length} modèles de CV disponibles
              </h2>

              <p className={cn("mt-0.5 text-[9px] text-indigo-950/65", "sm:text-[10px]")}>
                Faites défiler la liste puis sélectionnez librement votre modèle.
              </p>
            </div>
          </div>

          {templates.length > 3 && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  moveCarousel("previous");
                }}
                aria-label="Afficher les modèles précédents"
                className={cn(
                  "grid size-8 place-items-center rounded-full",
                  "border border-indigo-200 bg-white",
                  "text-indigo-700 transition-all",
                  "hover:border-indigo-400 hover:bg-indigo-50",
                  "hover:shadow-sm",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "sm:size-9",
                )}
              >
                <CaretLeftIcon aria-hidden size={17} weight="bold" />
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  moveCarousel("next");
                }}
                aria-label="Afficher les modèles suivants"
                className={cn(
                  "grid size-8 place-items-center rounded-full",
                  "border border-indigo-200 bg-white",
                  "text-indigo-700 transition-all",
                  "hover:border-indigo-400 hover:bg-indigo-50",
                  "hover:shadow-sm",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "sm:size-9",
                )}
              >
                <CaretRightIcon aria-hidden size={17} weight="bold" />
              </button>
            </div>
          )}
        </div>

        <div
          ref={carouselRef}
          className={cn(
            "-mx-1 mt-3 grid grid-flow-col",
            "gap-4 overflow-x-auto overflow-y-hidden",
            "px-1 pb-3 pt-1",
            "snap-x snap-mandatory scroll-smooth",
            "overscroll-x-contain",
            "auto-cols-[82%]",
            "xs:auto-cols-[68%]",
            "sm:auto-cols-[48%]",
            "lg:auto-cols-[31%]",
            "xl:auto-cols-[31%]",
            "[scrollbar-color:#818cf8_transparent]",
            "[scrollbar-width:thin]",
          )}
        >
          {templates.map((template) => {
            const templateId = template.templateId!;

            const thumbnailUrl = template.thumbnailUrl!;

            const active = selected === templateId;

            return (
              <button
                key={template.id}
                type="button"
                disabled={busy}
                aria-pressed={active}
                aria-label={`Sélectionner le modèle ${template.name}`}
                onClick={() => {
                  onSelect(templateId);
                }}
                className={cn(
                  "group relative min-w-0 snap-start",
                  "overflow-hidden rounded-xl border-2",
                  "bg-white text-left",
                  "transition-all duration-200",
                  "hover:-translate-y-0.5",
                  "hover:shadow-[0_10px_24px_rgba(79,70,229,0.12)]",
                  "focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-indigo-500 focus-visible:ring-offset-1",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  active
                    ? "border-emerald-500 shadow-[0_8px_20px_rgba(16,185,129,0.14)] ring-2 ring-emerald-100"
                    : "border-slate-200 hover:border-indigo-300",
                )}
              >
                <span className={cn("relative block aspect-[3/4]", "overflow-hidden bg-slate-100")}>
                  <img
                    src={thumbnailUrl}
                    alt={`Aperçu du modèle ${template.name}`}
                    loading="lazy"
                    draggable={false}
                    className={cn(
                      "h-full w-full object-cover object-top",
                      "transition-transform duration-300",
                      "group-hover:scale-[1.02]",
                    )}
                  />

                  {active && (
                    <span
                      className={cn(
                        "absolute right-2 top-2",
                        "grid size-7 place-items-center",
                        "rounded-full bg-white",
                        "shadow-[0_4px_12px_rgba(15,23,42,0.16)]",
                      )}
                    >
                      <CheckCircleIcon
                        aria-hidden
                        size={23}
                        weight="fill"
                        className="text-emerald-500"
                      />
                    </span>
                  )}
                </span>

                <span
                  className={cn(
                    "flex min-h-11 items-center",
                    "justify-between gap-2",
                    "border-t border-slate-100",
                    "bg-white px-3 py-2",
                  )}
                >
                  <span
                    className={cn(
                      "min-w-0 truncate",
                      "text-[10px] font-bold text-indigo-950",
                      "sm:text-xs",
                    )}
                  >
                    {template.name}
                  </span>

                  {active && (
                    <span
                      className={cn(
                        "shrink-0 rounded-full",
                        "bg-emerald-50 px-2 py-1",
                        "text-[8px] font-bold text-emerald-700",
                        "sm:text-[9px]",
                      )}
                    >
                      Sélectionné
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!selected || busy}
          onClick={onCreate}
          className={cn(
            "mt-3 inline-flex min-h-11 w-full",
            "items-center justify-center gap-3 rounded-xl",
            "bg-gradient-to-r",
            "from-indigo-700 via-blue-600 to-emerald-500",
            "px-5 text-xs font-bold text-white",
            "shadow-[0_8px_20px_rgba(79,70,229,0.20)]",
            "transition-all duration-200",
            "hover:-translate-y-0.5",
            "hover:shadow-[0_11px_25px_rgba(79,70,229,0.27)]",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-45",
            "disabled:hover:translate-y-0 disabled:hover:shadow-none",
            "sm:min-h-12 sm:text-sm",
          )}
        >
          {busy ? "Création du CV…" : "Utiliser ce modèle"}

          {!busy && <ArrowRightIcon aria-hidden size={19} weight="bold" className="shrink-0" />}
        </button>
      </div>
    </section>
  );
};
